import { indexOf } from 'lodash';
import { nanoid } from 'nanoid';
import React, { useState } from 'react';

import { useOutsideClick } from 'commons/hooks';
import {
  FloatingMenuActions,
  FloatingMenuContext,
  FloatingMenuNav,
} from 'craft/containers/FloatingMenu';
import { FlowStepTab } from 'craft/containers/FlowsFloatingMenu/FlowStepTab';
import { FlowStepTabContextMenu } from 'craft/containers/FlowsFloatingMenu/FlowStepTabContextMenu';
import { createFlowStep, useFlow } from 'craft/state';

export type TabContext = {
  dom: HTMLElement | null;
  id: string;
  index: number;
};

interface Props {
  flowId: string;
  menuPosition: 'top' | 'bottom';
  onToggleMenuPosition: VoidFunction;
}

export const FlowsFloatingMenu = React.memo(
  ({ flowId, menuPosition, onToggleMenuPosition }: Props) => {
    const { stepIds, onAddStep, activeStepId } = useFlow(flowId);

    const [flowTabContext, setFlowTabContext] = useState<TabContext | null>(null);

    useOutsideClick(flowTabContext?.dom || null, () => {
      setFlowTabContext(null);
    });

    const [floatingContextOpen, setFloatingContextOpen] = useState(false);

    useOutsideClick(document.getElementById('floating-menu-context-button') || null, () => {
      setFloatingContextOpen(false);
    });

    const [visibleTabs, setVisibleTabs] = useState<string[]>(stepIds);

    if (!stepIds) {
      return null;
    }

    return (
      <>
        <FloatingMenuNav
          tabIds={stepIds}
          activeTabIndex={activeStepId ? stepIds.indexOf(activeStepId) : undefined}
          setVisibleTabs={(tabs) => setVisibleTabs(tabs)}
        >
          {visibleTabs.map((stepId) => {
            const tabIndex = indexOf(stepIds, stepId);
            return (
              <FlowStepTab
                flowId={flowId}
                id={`floating-menu-tab-${tabIndex}`}
                // eslint-disable-next-line react/no-array-index-key
                key={`floating-menu-tab-${stepId}-${tabIndex}`}
                stepId={stepId}
                tooltipPlacement={menuPosition}
                isContextOpen={flowTabContext?.id}
                onContextClick={(dom: HTMLButtonElement | null) => {
                  if (floatingContextOpen) {
                    setFloatingContextOpen(false);
                  }

                  if (flowTabContext?.id !== stepId) {
                    setFlowTabContext({
                      id: stepId,
                      dom,
                      index: tabIndex,
                    });
                  }
                }}
              />
            );
          })}
        </FloatingMenuNav>

        <FloatingMenuActions
          onAdd={() => {
            onAddStep(createFlowStep(nanoid(6)));
          }}
          addLabel="Add Step"
          // Temporarily add button directly in floating menu actions until there are more actions to add to context menu
          menuPosition={menuPosition}
          onToggleMenuPosition={() => {
            if (flowTabContext) {
              setFlowTabContext(null);
            }
            onToggleMenuPosition();
          }}
          // onContextClick={() => {
          //   setFlowTabContext(null);
          //   setFloatingContextOpen(true);
          // }}
        />

        {floatingContextOpen && (
          <FloatingMenuContext
            dom={document.getElementById('floating-menu-context-button-wrapper')}
            menuPosition={menuPosition}
            onToggleMenuPosition={onToggleMenuPosition}
          />
        )}

        {flowTabContext && (
          <FlowStepTabContextMenu
            flowId={flowId}
            flowTabContext={flowTabContext}
            menuPosition={menuPosition}
          />
        )}
      </>
    );
  },
);

FlowsFloatingMenu.displayName = 'FlowFloatingMenu';
